/*
    Created by Phat Hoang Pham
    phat@launchdeck.org
*/

import React, { useState, useImperativeHandle, useRef } from 'react';
import { ActivityIndicator, Image, ScrollView, Text, TouchableOpacity, View } from 'react-native-web';
import PropTypes from 'prop-types';

// Child Components
import CalendarPickerModal from './component_calendar_picker';

// Custom Components
import { Button, InScreenModal, NavBar, TextBox } from '../../components';

// Utils
import { showAppError, showAlert } from '../../utils/util_helpers';

// APIs
import { importGoogleCalendar, exportAllCalendarEvents } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';
import moment from 'moment-timezone';
import { DatePickerModal } from 'react-native-paper-dates';
import { checkDate } from '../../modules/converters';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import LoadingView from '../../components/component_loading_view';
import { getDefaultSquareLocation } from '../../utils/util_main_app';

export default function ImportModal(props, ref) {
    const { openGoogleLoginModalFunc, onImportDone } = props;

    // region Refs
    const calendarPickerModal = useRef(null);
    // endregion

    // region Global State
    const [truckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [locationList] = useGlobalState(STATE_KEYS.LOCATION_LIST);
    // endregion

    // region Local State
    const [googleData, setGoogleData] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(moment().add(1, 'day').toDate());
    const [showStartDatePicker, setShowStartDatePicker] = useState(false);
    const [showEndDatePicker, setShowEndDatePicker] = useState(false);
    const [searchDoneBefore, setSearchDoneBefore] = useState(false);
    const [isLoadingEvents, setLoadingEvents] = useState(false);
    const [eventList, setEventList] = useState([]);
    const [isImportingAllEvents, setImportingAllEvents] = useState(false);
    const [isImportingSingleEvent, setImportingSingleEvent] = useState(null);
    // endregion

    // region Function - Show Modal
    const showModal = () => {
        setGoogleData(
            GLOBAL.DATA.USER['google_email'] ?
                {
                    email: GLOBAL.DATA.USER['google_email'],
                    selectedCalendar: { name: GLOBAL.DATA.USER['sync_calendar_name'], email: GLOBAL.DATA.USER['sync_calendar_email'] },
                    syncOn: !!GLOBAL.DATA.USER['is_sync_calendar']
                }
                :
                null
        );
        setVisible(true);
    };

    const hideModal = () => {
        setVisible(false);
    }
    // endregion

    // region Expose Methods
    useImperativeHandle(
        ref,
        () => ({
            showModal,
            hideModal
        }),
        [GLOBAL.DATA.USER]
    );
    // endregion

    // region Function - Import Events
    const goFetchEventList = (calendarID) => {
        setLoadingEvents(true);
        importGoogleCalendar(moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD'), calendarID)
            .then((eventList) => {
                const calendarArr = eventList.map((item) => Object.assign({}, item, { timeObj: checkDate(item, 'UTC') }));
                calendarArr.sort((a, b) => a.timeObj.startTime - b.timeObj.startTime);

                console.log('EVENT LIST', calendarArr);

                GLOBAL.DATA.CACHED_EVENT_LIST = calendarArr;

                setEventList(calendarArr);
                setLoadingEvents(false);
                if (calendarArr.length === 0) {
                    setSearchDoneBefore(false);
                    showAlert({ text: 'No event was found for the selected date range' });
                }
                else setSearchDoneBefore(true);
            })
            .catch((error) => {
                if (error.toString().includes('"code": 401') || error.toString().includes('sign out and sign back in to your google account')) {
                    showAlert({ text: 'Your Google login session has expired, please sign out and sign in again with your Google account' });
                }
                else showAppError(error);
            })
            .finally(() => setLoadingEvents(false));
    }
    // endregion

    // region Function - Import All Events from List
    const goImportEvent = ({ eventIndex = null, importAll = false }) => {
        showAlert({
            text: importAll ?
                'By selecting Import All, you are allowing Truckster to post all events, whether public or private, for the time period selected.<br/><br/>Please ensure that your information is accurate including addresses and times.'
                :
                'Confirm importing this event to your Truckster schedule?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    const sendingList = (importAll ? eventList : [eventList[eventIndex]]).map((item) => (Object.assign({}, item, {
                        'activity': null,
                        'brewery': null,
                        'squareup_location_id': getDefaultSquareLocation()?.['squareup_location_id'],
                        'start_time': moment.unix(item.timeObj.startTime).utc().format('YYYY-MM-DD HH:mm:ss'),
                        'end_time': moment.unix(item.timeObj.endTime).utc().format('YYYY-MM-DD HH:mm:ss'),
                        'frequency': 'ONCE'
                    })));

                    importAll ? setImportingAllEvents(true) : setImportingSingleEvent(eventIndex);
                    exportAllCalendarEvents(sendingList)
                        .then((resultList) => {
                            onImportDone(resultList);
                            if (importAll) {
                                hideModal();
                                setSearchDoneBefore(false);
                                setEventList([]);
                                GLOBAL.DATA.CACHED_EVENT_LIST = [];
                            }
                            else {
                                const newEventList = eventList.filter((item, index) => index !== eventIndex);
                                setEventList(newEventList);
                                GLOBAL.DATA.CACHED_EVENT_LIST = newEventList;
                                if (newEventList.length === 0) setSearchDoneBefore(false);
                            }
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => {
                            setImportingAllEvents(false);
                            setImportingSingleEvent(null);
                        });
                }
            }
        })
    }
    // endregion

    // region Render Time Input
    const renderTimeInput = ({ title = '', value = '', onPress = () => {}, isLeft = false }) => (
        <TextBox
            title={title}
            value={value}
            onPress={onPress}
            customOnPressIcon={'event'}
            style={[{ flex: 1, marginTop: 0 }, isLeft ? { marginRight: 4 } : { marginLeft: 4 }]}
        />
    )
    // endregion

    // region Render Start Date Picker
    const renderStartDatePicker = () => (
        <DatePickerModal
            locale={'en'}
            mode={'single'}
            visible={showStartDatePicker}
            date={startDate}
            saveLabelDisabled={true}
            validRange={{ startDate: moment().startOf('day').toDate() }}
            onChange={({ date }) => {
                const newDate = moment(startDate).clone();
                newDate.set('date', date.getDate());
                newDate.set('month', date.getMonth());
                newDate.set('year', date.getFullYear());
                setStartDate(newDate.startOf('day').toDate());
                setShowStartDatePicker(false);

                // If start date after end date
                if (newDate.isAfter(moment(endDate))) {
                    setEndDate(newDate.clone().add(1, 'day').toDate());
                }
            }}
            onDismiss={() => setShowStartDatePicker(false)}
        />
    )
    // endregion

    // region Render End Date Picker
    const renderEndDatePicker = () => (
        <DatePickerModal
            locale={'en'}
            mode={'single'}
            visible={showEndDatePicker}
            date={endDate}
            saveLabelDisabled={true}
            validRange={{ startDate: moment().startOf('day').add(1, 'day').toDate() }}
            onChange={({ date }) => {
                const newDate = moment(endDate).clone();
                newDate.set('date', date.getDate());
                newDate.set('month', date.getMonth());
                newDate.set('year', date.getFullYear());
                setEndDate(newDate.endOf('day').toDate());
                setShowEndDatePicker(false);

                // If end date before start date
                if (newDate.isBefore(moment(startDate))) {
                    setStartDate(newDate.clone().subtract(1, 'day').toDate());
                }
            }}
            onDismiss={() => setShowEndDatePicker(false)}
        />
    )
    // endregion

    // region Render Date Range View
    const renderDateRangeView = () => (
        googleData &&
        <View style={{ flex: 1, width: '100%' }}>
            {/* Start Time / End Time */}
            <View pointerEvents={isLoadingEvents ? 'none' : 'auto'} style={{ flexDirection: 'row', alignItems: 'flex-start', opacity: isLoadingEvents ? 0.7 : 1 }}>
                {/* Start Time */}
                {renderTimeInput({
                    value: moment(startDate).format('MMM D, YYYY'),
                    title: 'Start Date',
                    isLeft: true,
                    onPress: () => setShowStartDatePicker(true)
                })}

                {/* Start Date Time Picker */}
                {renderStartDatePicker()}

                {/* End Time */}
                {renderTimeInput({
                    value: moment(endDate).format('MMM D, YYYY'),
                    title: 'End Date',
                    isLeft: false,
                    onPress: () => setShowEndDatePicker(true)
                })}

                {/* End Date Time Picker */}
                {renderEndDatePicker()}

                {/* Search Button */}
                <Button
                    text={'Find Events'}
                    style={{ backgroundColor: GLOBAL.COLOR.BASIC.BLACK_TWO, marginLeft: 16, width: 250, height: 74 }}
                    useFeather={true}
                    image={'search'}
                    imageSize={24}
                    onPress={() => calendarPickerModal.current?.showModal()}
                    isLoading={isLoadingEvents}
                />
            </View>
        </View>
    )
    // endregion

    // region Render Event Item
    const renderEventItem = (item, eventIndex) => (
        <View
            key={`eventItem_${item.id}`}
            style={{
                borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, borderRadius: 8,
                width: '100%', backgroundColor: 'white', flexDirection: 'row', alignItems: 'center',
                overflow: 'hidden', marginBottom: 16
            }}>
            {/* Main Data */}
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', padding: 24 }}>
                {/* Date String */}
                <View style={{ alignItems: 'center' }}>
                    {/* Month */}
                    <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, fontSize: 16 }}>
                        {moment.unix(item.timeObj.startTime).format('MMM')}
                    </Text>

                    {/* Year */}
                    <Text style={{ fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 24, letterSpacing: -0.2, marginTop: 8 }}>
                        {moment.unix(item.timeObj.startTime).format('DD')}
                    </Text>
                </View>

                {/* Event Info */}
                <View style={{ flex: 1, marginLeft: 24, justifyContent: 'flex-start' }}>
                    {/* Time */}
                    <Text style={{ fontSize: 16, fontWeight: '500', color: GLOBAL.COLOR.MAIN }}>
                        {moment.unix(item.timeObj.startTime).format('h:mm A') + ' - ' + moment.unix(item.timeObj.endTime).format('h:mm A')}
                    </Text>

                    {/* Location Name */}
                    <Text style={{ fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 20, marginTop: 8 }} numberOfLines={1}>
                        {item['location_name']}
                    </Text>

                    {/* Address */}
                    <Text style={{ fontWeight: '400', color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 16, marginTop: 8 }} numberOfLines={1}>
                        {item['address']}
                    </Text>
                </View>
            </View>

            {/* Import Button */}
            <TouchableOpacity
                disabled={isImportingSingleEvent === eventIndex}
                onPress={() => goImportEvent({ eventIndex })}
                style={{ width: 100, height: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: GLOBAL.COLOR.BASIC.UI_GREEN }}>
                {
                    (isImportingSingleEvent === eventIndex) ?
                        <ActivityIndicator color={'white'} size={28} />
                        :
                        <FeatherIcon name={'download'} color={'white'} size={28} />
                }
            </TouchableOpacity>
        </View>
    )
    // endregion

    // region Render Event List
    const renderEventList = () => (
        (googleData && searchDoneBefore) &&
        (
            isLoadingEvents ?
                <LoadingView isLoading={true} description={'Finding Events...'} />
                :
                <View style={{ flex: 1, width: '100%', backgroundColor: 'white', marginTop: 24 }}>
                    {/* Title */}
                    <Text style={{ fontWeight: '900', fontSize: 26, textAlign: 'left', margin: 32, marginBottom: 0 }}>
                        Found Events ({eventList.length})
                    </Text>

                    {/* Description */}
                    <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 32, paddingTop: 24 }}>
                        <Text style={{ fontSize: 18, color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'left', lineHeight: 28, flex: 1 }}>
                            Click the import icon on each of the events you want to import to add them to your Truckster schedule or hit Import All to quickly add all of them at once.
                        </Text>

                        {/* Import All Button */}
                        {
                            !isImportingSingleEvent &&
                            <Button
                                text={'Import All'}
                                style={{ width: 250, marginLeft: 24 }}
                                image={'download-cloud'}
                                imageSize={24}
                                useFeather={true}
                                isLoading={isImportingAllEvents}
                                onPress={() => goImportEvent({ importAll: true })}
                            />
                        }
                    </View>

                    {/* List */}
                    <View
                        pointerEvents={(isImportingAllEvents || isImportingSingleEvent) ? 'none' : 'auto'}
                        style={{ flex: 1, width: '100%', paddingHorizontal: 32, paddingTop: 32, backgroundColor: 'white', opacity: (isImportingAllEvents || isImportingSingleEvent) ? 0.7 : 1 }}>
                        {eventList.map((item, index) => renderEventItem(item, index))}
                    </View>
                </View>
        )
    )
    // endregion

    // region MAIN RENDER
    return(
        <InScreenModal visible={visible}>
            {/* Nav Bar */}
            <NavBar title={'Auto Sync'} leftButtonOnPress={() => hideModal()} />

            {/* Main View */}
            <ScrollView
                style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                <View style={{ alignSelf: 'stretch', backgroundColor: 'white', padding: 32 }}>
                    {/* Title */}
                    <Text style={{ fontWeight: '900', fontSize: 28, textAlign: 'left' }}>
                        Import Events from Google Calendar
                    </Text>

                    {/* Description */}
                    <Text style={{ marginTop: 16, fontSize: 18, color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'left', lineHeight: 26, marginBottom: 32 }}>
                        {
                            googleData ?
                                'Select a start date and end date to find and import events from your Google Calendar to your food truck\'s schedule (current and future events only)'
                                :
                                'Sign in with your Google account to import your events to Truckster Vendor'
                        }
                    </Text>

                    {/* Current Signed In Email */}
                    {
                        googleData &&
                        <TouchableOpacity
                            onPress={() => openGoogleLoginModalFunc(() => showModal())}
                            style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 32 }}>
                            <Image
                                source={require('../../images/image_google_color.png')}
                                style={{ width: 24, height: 24 }}
                            />

                            <Text style={{ fontSize: 18, marginLeft: 12 }}>
                                Signed in as{' '}
                                <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                    {googleData.email}
                                </Text>
                            </Text>
                        </TouchableOpacity>
                    }

                    {/* Sign In Button */}
                    {
                        googleData ?
                            renderDateRangeView()
                            :
                            <Button
                                text={'Sign to your Google account'}
                                style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE }}
                                useFeather={true}
                                image={require('../../images/iconSocialGoogle.png')}
                                imageSize={24}
                                onPress={() => openGoogleLoginModalFunc(() => showModal())}
                                isLoading={isLoading}
                            />
                    }
                </View>

                {/* Event List */}
                {renderEventList()}

                {/* Calendar Picker */}
                <CalendarPickerModal
                    ref={calendarPickerModal}
                    onSelected={(email, name) => {}}
                />

                {/* Calendar Picker */}
                <CalendarPickerModal
                    ref={calendarPickerModal}
                    onSelected={(calendarID) => goFetchEventList(calendarID)}
                />
            </ScrollView>
        </InScreenModal>
    )
    // endregion
}

ImportModal = React.forwardRef(ImportModal);

// region Props
ImportModal.propTypes = {
    openGoogleLoginModalFunc: PropTypes.func,
    onImportDone: PropTypes.func
}

ImportModal.defaultProps = {
    openGoogleLoginModalFunc: () => {},
    onImportDone: () => {}
}
// endregion
